import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import * as Clipboard from 'expo-clipboard';
import axios from 'axios';
import { useAuthStore } from '../../src/stores/authStore';
import { useEncryptionStore } from '../../src/stores/encryptionStore';
import { encryptionApi, teamsApi } from '../../src/api/client';
import {
  clearStoredKey,
  generateRecoveryPhrase,
  generateSalt,
  hashRecoveryPhrase,
  deriveKeyMaterial,
} from '../../src/utils/encryption';
import { generateTeamKey, wrapTeamKey } from '../../src/utils/teamCrypto';
import { arrayBufferToBase64 } from '../../src/utils/encoding';
import { clearSeed, importAndStoreRawKey, storeSeed } from '../../src/utils/crypto';
import { isCryptoEnvironmentError } from '../../src/utils/cryptoErrors';
import { CLIPBOARD_FEEDBACK_MS } from '../../src/constants';
import { useTheme } from '../../src/theme/ThemeContext';
import { useThemedStyles } from '../../src/theme/useThemedStyles';
import type { Theme } from '../../src/theme/types';

export default function EncryptionSetupScreen() {
  const router = useRouter();
  const logout = useAuthStore((s) => s.logout);
  const { recoveryPhrase, setRecoveryPhrase } = useEncryptionStore();

  const [step, setStep] = useState<'generating' | 'display' | 'error'>('generating');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [salt, setSalt] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [clipboardHoldsPhrase, setClipboardHoldsPhrase] = useState(false);
  const [wrappedTeamKey, setWrappedTeamKey] = useState<string | null>(null);
  const [publicKeyB64, setPublicKeyB64] = useState<string | null>(null);
  const initialized = useRef(false);
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();

  const runInit = async () => {
    setStep('generating');
    setError(null);

    try {
      const phrase = generateRecoveryPhrase();
      const newSalt = generateSalt();
      setSalt(newSalt);
      setRecoveryPhrase(phrase);

      const phraseString = phrase.join(' ');
      const { rawSeed, x25519PublicKey } = await deriveKeyMaterial(phraseString, newSalt);

      // Generate a random team key and wrap it with the X25519 public key
      const teamKey = generateTeamKey();
      const sealedBlob = wrapTeamKey(teamKey, x25519PublicKey);

      // Store the TEAM KEY as the active device encryption key. This is the
      // invariant every other provisioning path has to match: the device-key
      // slot holds the personal team key, never the PBKDF2 output.
      const userId = useAuthStore.getState().user?.id;
      if (!userId) throw new Error('You are not signed in.');
      await importAndStoreRawKey(teamKey, userId);
      useEncryptionStore.getState().setHasLocalKey(true);

      // Persist PBKDF2 seed for QR transfer + X25519 derivation
      await storeSeed(rawSeed, userId);

      // Store wrapped key and public key for the submit step
      setWrappedTeamKey(arrayBufferToBase64(sealedBlob));
      setPublicKeyB64(arrayBufferToBase64(x25519PublicKey));

      setStep('display');
    } catch (err) {
      console.error('E2EE setup failed:', err);
      // Nothing above touches the network, so "check your connection" was
      // never the reason. What fails here is the device: WebCrypto missing on
      // an http origin, or storage refusing the write.
      setError(
        isCryptoEnvironmentError(err)
          ? err.message
          : 'Failed to set up encryption on this device. Please try again.'
      );
      setStep('error');
    }
  };

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    runInit();
  }, [setRecoveryPhrase]);

  const handleCopyPhrase = async () => {
    if (!recoveryPhrase) return;
    const numbered = recoveryPhrase.map((w, i) => `${i + 1}. ${w}`).join('\n');
    await Clipboard.setStringAsync(numbered);
    setCopied(true);
    setClipboardHoldsPhrase(true);
    setTimeout(() => setCopied(false), CLIPBOARD_FEEDBACK_MS);
    // Native can clear the clipboard on a timer. The web cannot: WebKit
    // refuses clipboard writes that do not come from a user gesture, so the
    // 15-second auto-clear this used to run there was rejected silently and
    // the phrase stayed on the clipboard indefinitely — a documented
    // safeguard that did not exist. On web the user clears it with the
    // button below, which is a gesture.
    if (Platform.OS !== 'web') {
      setTimeout(async () => {
        try {
          await Clipboard.setStringAsync('');
          setClipboardHoldsPhrase(false);
        } catch {
          // Clipboard may already be cleared or app backgrounded
        }
      }, 15_000);
    }
  };

  const handleClearClipboard = async () => {
    try {
      await Clipboard.setStringAsync('');
      setClipboardHoldsPhrase(false);
    } catch (err) {
      console.warn('Could not clear the clipboard:', err);
    }
  };

  const handleContinue = async () => {
    if (!recoveryPhrase) return;

    setError(null);
    setIsSubmitting(true);

    try {
      const phraseString = recoveryPhrase.join(' ');
      const phraseHash = await hashRecoveryPhrase(phraseString);

      await encryptionApi.setup({
        salt,
        recovery_phrase_hash: phraseHash,
        e2ee_public_key: publicKeyB64!,
        personal_team_wrapped_key: wrappedTeamKey!,
      });

      // Navigate to setup-complete screen. That screen sets e2eeSetupComplete
      // when the user taps "Start Using App", which triggers root layout
      // to route to the main tabs.
      router.replace('/(setup)/complete');
    } catch (err) {
      // 409 = E2EE already set up on server (e.g. previous attempt succeeded
      // but navigation failed). The local key from runInit() doesn't match
      // the server's salt — redirect to restore so user enters their original
      // recovery phrase to derive the correct key.
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        const encStore = useEncryptionStore.getState();
        encStore.setIsNewUser(false);
        encStore.setE2EEComplete(true);
        encStore.setHasLocalKey(false);
        // Clear BOTH records runInit wrote, and finish before leaving. Only the
        // key used to go, in a floating promise that raced the navigation — and
        // the seed it left behind derives the wrong X25519 identity, so every
        // later team-key unwrap failed silently until a restore replaced it.
        const userId = useAuthStore.getState().user?.id;
        if (userId) {
          try {
            await Promise.all([clearStoredKey(userId), clearSeed(userId)]);
          } catch (clearErr) {
            console.warn('Could not clear the provisional key material:', clearErr);
          }
        }
        router.replace('/(setup)/restore');
        return;
      }
      console.error('E2EE setup finalization failed:', err);
      setError('Failed to complete setup. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === 'generating') {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={styles.generatingTitle}>Setting Up Encryption</Text>
        <Text style={styles.generatingSubtitle}>
          Generating your recovery phrase and encryption key...
        </Text>
      </View>
    );
  }

  if (step === 'error') {
    return (
      <View style={styles.centered}>
        <View style={styles.errorCard}>
          <Text style={styles.errorIconLarge}>{'\u26A0'}</Text>
          <Text style={styles.title}>Setup Failed</Text>
          <Text style={styles.subtitle}>
            {error || 'Something went wrong during encryption setup.'}
          </Text>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={runInit}
            activeOpacity={0.7}
            accessibilityLabel="Retry encryption setup"
            accessibilityRole="button"
            testID="encryption.retry-button"
          >
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={logout}
            style={styles.logoutButton}
            accessibilityLabel="Log Out"
            accessible={true}
            accessibilityRole="button"
            testID="encryption.logout-button-error"
          >
            <Text style={styles.logoutText}>Log Out</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  if (step === 'display') {
    return (
      <View style={styles.container}>
        <ScrollView
          contentContainerStyle={styles.scrollContent}
        >
          <View style={styles.card}>
            <View style={styles.iconContainer}>
              <Text style={styles.iconText} accessible={false}>{'\u{1F512}'}</Text>
            </View>
            <Text style={styles.title}>Your Recovery Phrase</Text>

            <View style={styles.warningBanner}>
              <Text style={styles.warningBannerText}>
                Save this 24-word recovery phrase. If you lose access to all your devices, this is the only way to recover your data.
              </Text>
            </View>

            <View style={styles.phraseGrid}>
              {recoveryPhrase?.map((word, index) => (
                <View key={index} style={styles.phraseItem}>
                  <Text style={styles.phraseNumber}>{index + 1}.</Text>
                  <Text style={styles.phraseWord} testID={`encryption.phrase-word-${index + 1}`}>{word}</Text>
                </View>
              ))}
            </View>

            <TouchableOpacity
              style={styles.copyButton}
              onPress={handleCopyPhrase}
              activeOpacity={0.7}
              accessibilityLabel="Copy recovery phrase to clipboard"
              accessibilityRole="button"
              testID="encryption.copy-button"
            >
              <Text style={styles.copyButtonText}>
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </Text>
            </TouchableOpacity>

            {clipboardHoldsPhrase && (
              <TouchableOpacity
                style={styles.clearClipboardButton}
                onPress={handleClearClipboard}
                activeOpacity={0.7}
                accessibilityLabel="Clear the recovery phrase from the clipboard"
                accessibilityRole="button"
                testID="encryption.clear-clipboard-button"
              >
                <Text style={styles.clearClipboardText}>
                  Clear Clipboard
                </Text>
              </TouchableOpacity>
            )}

            {error && (
              <View style={styles.errorBox}>
                <Text style={styles.errorText}>{error}</Text>
              </View>
            )}

            <TouchableOpacity
              style={[styles.button, isSubmitting && styles.buttonDisabled]}
              onPress={handleContinue}
              disabled={isSubmitting}
              activeOpacity={0.7}
              accessibilityLabel="I have saved my recovery phrase"
              accessibilityRole="button"
              accessibilityState={{ disabled: isSubmitting }}
              testID="encryption.saved-button"
            >
              {isSubmitting ? (
                <ActivityIndicator size="small" color={colors.textOnPrimary} />
              ) : (
                <Text style={styles.buttonText}>I've Saved My Recovery Phrase</Text>
              )}
            </TouchableOpacity>

            <TouchableOpacity onPress={logout} style={styles.logoutButton} accessibilityLabel="Log Out" accessible={true} accessibilityRole="button" testID="encryption.logout-button-display">
              <Text style={styles.logoutText}>Log Out</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>
    );
  }

  return null;
}

const createStyles = (theme: Theme) => ({
  container: { flex: 1 as const, backgroundColor: theme.colors.brandedBackground },
  scrollContent: { flexGrow: 1 as const, justifyContent: 'center' as const, paddingHorizontal: 24, paddingVertical: 48 },
  centered: { flex: 1 as const, justifyContent: 'center' as const, alignItems: 'center' as const, backgroundColor: theme.colors.brandedBackground, paddingHorizontal: 24 },
  generatingTitle: { fontSize: 18, fontWeight: '600' as const, color: theme.colors.textOnPrimary, marginTop: 20 },
  generatingSubtitle: { fontSize: 14, color: theme.colors.primaryLight, marginTop: 8, textAlign: 'center' as const },
  card: { backgroundColor: theme.colors.surface, borderRadius: 16, padding: 24, shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.1, shadowRadius: 12, elevation: 5 },
  iconContainer: { width: 56, height: 56, borderRadius: 28, backgroundColor: theme.colors.primaryBg, justifyContent: 'center' as const, alignItems: 'center' as const, alignSelf: 'center' as const, marginBottom: 16 },
  iconText: { fontSize: 28 },
  title: { fontSize: 20, fontWeight: '700' as const, color: theme.colors.textPrimary, textAlign: 'center' as const, marginBottom: 8 },
  subtitle: { fontSize: 14, color: theme.colors.textMuted, textAlign: 'center' as const, marginBottom: 20, lineHeight: 20 },
  warningBanner: { backgroundColor: theme.colors.errorBg, borderWidth: 1, borderColor: theme.colors.errorBorder, borderRadius: 10, padding: 12, marginBottom: 12, width: '100%' as const },
  warningBannerText: { color: theme.colors.errorText, fontSize: 14, fontWeight: '700' as const, textAlign: 'center' as const },
  phraseGrid: { flexDirection: 'row' as const, flexWrap: 'wrap' as const, justifyContent: 'space-between' as const, marginBottom: 16, width: '100%' as const },
  phraseItem: { flexDirection: 'row' as const, alignItems: 'center' as const, width: '48%' as const, backgroundColor: theme.colors.bgSecondary, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8, marginBottom: 8 },
  phraseNumber: { fontSize: 13, fontWeight: '600' as const, color: theme.colors.textDisabled, width: 28 },
  phraseWord: { fontSize: 15, fontWeight: '600' as const, color: theme.colors.textPrimary },
  copyButton: { backgroundColor: theme.colors.primaryBg, borderWidth: 1, borderColor: theme.colors.primaryLight, borderRadius: 10, paddingVertical: 12, alignItems: 'center' as const, width: '100%' as const, marginBottom: 12 },
  copyButtonText: { color: theme.colors.primary, fontSize: 14, fontWeight: '600' as const },
  clearClipboardButton: { borderWidth: 1, borderColor: theme.colors.borderMedium, borderRadius: 10, paddingVertical: 10, alignItems: 'center' as const, width: '100%' as const, marginBottom: 12 },
  clearClipboardText: { color: theme.colors.textSecondary, fontSize: 14, fontWeight: '600' as const },
  errorBox: { backgroundColor: theme.colors.errorBg, borderWidth: 1, borderColor: theme.colors.errorBorder, borderRadius: 10, padding: 14, marginBottom: 12 },
  errorText: { color: theme.colors.errorText, fontSize: 14, lineHeight: 20 },
  button: { backgroundColor: theme.colors.primary, borderRadius: 10, paddingVertical: 14, alignItems: 'center' as const, justifyContent: 'center' as const, minHeight: 48, marginTop: 8 },
  buttonDisabled: { backgroundColor: theme.colors.borderMedium },
  buttonText: { color: theme.colors.textOnPrimary, fontSize: 16, fontWeight: '700' as const },
  logoutButton: { alignItems: 'center' as const, marginTop: 12 },
  logoutText: { fontSize: 14, color: theme.colors.danger, fontWeight: '600' as const },
  errorCard: { width: '100%' as const, maxWidth: 400, backgroundColor: theme.colors.surface, borderRadius: 16, padding: 24, alignItems: 'center' as const, shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.1, shadowRadius: 12, elevation: 5 },
  errorIconLarge: { fontSize: 48, color: theme.colors.warning, marginBottom: 12 },
  retryButton: { width: '100%' as const, backgroundColor: theme.colors.primary, borderRadius: 10, paddingVertical: 14, alignItems: 'center' as const, justifyContent: 'center' as const, minHeight: 48, marginTop: 8 },
  retryButtonText: { color: theme.colors.textOnPrimary, fontSize: 16, fontWeight: '700' as const },
});
